import { useState, useEffect, useRef, useCallback } from 'react';
import { loadSearchResultFromStorage } from '../../../store/insuranceSearchStore';
import { useParams } from 'react-router-dom';

/**
 * セッションストレージのキー
 * ブラウザリロード時に検索結果を復元するために使用
 */
const SESSION_STORAGE_KEY = 'insuranceHikiukeDetailSelectedResult';

/**
 * セッションストレージから検索結果を読み込む
 * 
 * ブラウザリロード時に以前選択した検索結果を復元するために使用。
 * パースエラー時はストレージをクリアして安全に失敗する。
 * 
 * @returns {Object|null} 保存された結果データ、存在しない場合はnull
 */
const loadResultFromSessionStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const storedValue = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (error) {
    console.error('sessionStorageからの読み込みに失敗しました:', error);
    // 破損したデータをクリア
    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (removeError) {
      console.error('sessionStorageのクリアに失敗しました:', removeError);
    }
    return null;
  }
};

/**
 * セッションストレージに検索結果を保存する
 * 
 * ブラウザリロード後も選択した検索結果を維持するために使用。
 * 
 * @param {Object} resultData - 保存する検索結果データ
 */
const saveResultToSessionStorage = (resultData) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(resultData));
  } catch (error) {
    console.error('sessionStorageへの保存に失敗しました:', error);
  }
};

/**
 * セッション管理と初期化を行うカスタムフック
 * 
 * 引受目安詳細画面のセッション状態を管理する。
 * 検索結果データの読み込み、保存、復元、およびドキュメント情報の管理を行う。
 * 
 * データソースの優先順位:
 * 1. sessionStorage（ブラウザリロード時の復元用）
 * 2. insuranceSearchStore（検索画面からの遷移時）
 * 
 * @param {Object} options - オプション
 * @param {Function} options.onError - エラー発生時のコールバック関数
 * @param {Function} options.setCurrentPage - PDFの現在ページを設定する関数
 * @param {Function} options.initializeHighlight - ハイライト情報を初期化する関数
 * @returns {Object} セッション関連の状態とハンドラ
 * @returns {Object|null} returns.detailResult - 検索結果の詳細データ
 * @returns {string} returns.blobName - PDF BlobのURL/パス
 * @returns {string|null} returns.currentQueryId - 現在のクエリID（フィードバック用）
 * @returns {Object} returns.documentInfo - ドキュメント情報（保険会社名など）
 * @returns {boolean} returns.isMobile - モバイル表示かどうか（画面幅1024px以下）
 * @returns {boolean} returns.isDocInfoAccordionOpen - ドキュメント情報アコーディオンの開閉状態
 * @returns {Function} returns.toggleDocInfoAccordion - アコーディオン開閉トグル関数
 * @returns {Function} returns.setDetailResult - 詳細結果を設定する関数
 * @returns {Function} returns.setBlobName - BlobNameを設定する関数
 */
export const useInsuranceHikiukeSession = ({
  onError,
  setCurrentPage,
  initializeHighlight,
}) => {
  /**
   * 検索結果の詳細データ
   * APIから取得した検索結果の全情報を保持
   */
  const [detailResult, setDetailResult] = useState(null);

  /**
   * PDF BlobのURL/パス
   * PDFViewerコンポーネントでPDFを読み込むために使用
   */
  const [blobName, setBlobName] = useState('');

  /**
   * 現在のクエリID
   * フィードバック送信時にどの検索に対するフィードバックかを識別するために使用
   */
  const [currentQueryId, setCurrentQueryId] = useState(null);

  /**
   * ドキュメント情報
   * ツールバーに表示する保険会社名、傷病名などの情報
   */
  const [documentInfo, setDocumentInfo] = useState({
    insuranceCompany: '',
    diseaseName: '',
  });

  /**
   * モバイル表示フラグ
   * 画面幅1024px以下の場合にtrueになり、UIレイアウトを切り替える
   */
  const [isMobile, setIsMobile] = useState(false);

  /**
   * ドキュメント情報アコーディオンの開閉状態
   * モバイル表示時にドキュメント情報を折りたたみ表示するために使用
   */
  const [isDocInfoAccordionOpen, setIsDocInfoAccordionOpen] = useState(false);

  /**
   * デフォルトのドキュメントタイトルを保持するref
   * コンポーネントのアンマウント時に元のタイトルに復元するために使用
   */
  const defaultDocumentTitleRef = useRef(
    typeof document !== 'undefined' ? document.title : 'ASFiNDER'
  );

  /**
   * ブラウザタブのタイトルを更新するEffect
   * 保険会社名と傷病名が変更されたらタイトルを「{保険会社名} - {傷病名} | ASFiNDER」形式に更新
   * アンマウント時は元のタイトルに復元
   */
  useEffect(() => {
    // 既定のタイトル(ASFiNDER)を取得
    const defaultTitle = defaultDocumentTitleRef.current || 'ASFiNDER';

    // 保険会社名を取得
    const companyName = documentInfo.insuranceCompany
      ? String(documentInfo.insuranceCompany).trim()
      : '';
    const displayName = companyName || '保険会社不明';

    // 傷病名を取得
    const diseaseName = documentInfo.diseaseName
      ? String(documentInfo.diseaseName).trim()
      : '';
    const displayDisease = diseaseName || '傷病名不明';

    // タイトルを更新
    if (typeof document !== 'undefined') {
      document.title = `${displayName} - ${displayDisease} | ${defaultTitle}`;
      return () => {
        document.title = defaultTitle;
      };
    }
  }, [documentInfo.insuranceCompany, documentInfo.diseaseName]);

  /**
   * 画面幅に応じてモバイル表示を切り替えるEffect
   * リサイズイベントを監視し、1024px以下でモバイルモードに切り替え
   */
  useEffect(() => {
    // windowsのresizeイベントハンドラ
    // > リサイズに応じてモバイル判定用のフラグを更新
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 1024);
    };

    // 初期値を設定
    handleResize();

    // イベントリスナー登録
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  /**
   * 検索結果データを処理してステートに反映する
   * 
   * PDFのURL、ドキュメント情報、初期ページ番号を抽出して各ステートに設定。
   * PDFのURLが存在しない場合はエラーをコールバック。
   * 
   * @param {Object} resultData - 検索結果データ
   */
  const processSearchResult = useCallback((resultData) => {
    try {
      // PDFのURLを正規化して設定
      if (resultData.pdfUrl) {
        const normalizedPath = resultData.pdfUrl.replace(/\\/g, '/');
        setBlobName(normalizedPath);
      } else {
        // PDFのURLが存在しない場合はエラーをコールバック
        if (onError) {
          onError('PDFのURLがありません');
        }
        return;
      }

      // ドキュメント情報（保険会社名、傷病名）を設定
      setDocumentInfo({
        insuranceCompany: resultData.companyName || '',
        diseaseName: resultData.diseaseName || '',
      });

      // 初期表示ページを設定（検索でヒットしたページを表示）
      if (resultData.pages && setCurrentPage) {
        setCurrentPage(resultData.pages);
      }
    } catch (error) {
      // エラーはonErrorコールバックで処理
      if (onError) {
        onError('検索結果データの処理中にエラーが発生しました', error);
      }
    }
  }, [onError, setCurrentPage]);

  /**
   * URLパラメータからIDを取得
   * 
   * ローカルストレージやセッションストレージから検索結果を特定するために使用
   */
  const { id } = useParams();

  /**
   * 検索結果データから詳細画面を初期化する
   * 
   * 検索結果データを受け取り、各種ステートを初期化する。
   * persistSessionがtrueの場合はsessionStorageにも保存する。
   * 
   * @param {Object} results - 検索結果データ
   * @param {Object} options - オプション
   * @param {boolean} options.persistSession - sessionStorageに保存するかどうか
   * @returns {boolean} 初期化に成功した場合はtrue、失敗した場合はfalse
   */
  const initializeDetailFromResult = useCallback((results, { persistSession } = { persistSession: false }) => {
    const resultData = results[id];
    if (!resultData) {
      return false;
    }

    // セッションストレージへの保存（ブラウザリロード対策）
    if (persistSession) {
      saveResultToSessionStorage(results);
    }

    // 検索結果データをステートに設定
    setDetailResult(resultData);

    // クエリIDを設定（フィードバック送信時に使用）
    if (resultData.queryId) {
      setCurrentQueryId(resultData.queryId);
    }

    // ハイライト情報の初期化（検索でヒットした箇所を強調表示）
    if (initializeHighlight) {
      initializeHighlight(resultData);
    }

    // 検索結果データの処理
    processSearchResult(resultData);

    return true;
  }, [initializeHighlight, processSearchResult]);

  /**
   * react-pdfライブラリの警告抑制Effect
   * 
   * pdfjs-distのバージョン不一致警告を抑制する。
   * 実際の動作に影響はないが、コンソールが汚れるため抑制。
   */
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = function (message) {
      if (typeof message === 'string' && message.includes('version') && message.includes('match')) {
        return;
      }
      originalWarn.apply(console, arguments);
    };

    // クリーンアップ: console.warnを元に戻す
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  /**
   * セッション情報読み込みEffect
   * 
   * 以下の優先順位でデータソースから検索結果を読み込む:
   * 1. sessionStorage（ブラウザリロード時の復元）
   * 2. insuranceSearchStore（検索画面からの遷移）
   * 
   * どちらからも読み込めない場合はエラーをコールバック。
   */
  useEffect(() => {
    try {
      // 優先度順にデータを取得（sessionStorage > insuranceSearchStore）
      const sessionResult = loadResultFromSessionStorage();
      const result = sessionResult || loadSearchResultFromStorage();

      if (result) {
        // sessionResultがあればpersist不要（既にsessionStorageにある）
        // sessionResultがなければpersist必要（新規遷移なのでsessionStorageに保存）
        initializeDetailFromResult(result, { persistSession: !sessionResult });
      } else {
        onError?.('検索結果データが見つかりません。検索画面から選択してください。');
      }
    } catch (error) {
      onError?.('初期化処理中にエラーが発生しました', error);
    }
  }, [initializeDetailFromResult, onError]);

  /**
   * ドキュメント情報アコーディオンの開閉を切り替える
   * モバイル表示時にドキュメント情報の表示/非表示を切り替えるために使用
   */
  const toggleDocInfoAccordion = useCallback(() => {
    setIsDocInfoAccordionOpen((prev) => !prev);
  }, []);

  return {
    detailResult,
    blobName,
    currentQueryId,
    documentInfo,
    isMobile,
    isDocInfoAccordionOpen,
    toggleDocInfoAccordion,
    setDetailResult,
    setBlobName,
  };
};

export default useInsuranceHikiukeSession;
